import React from 'react';

interface LoadingComponentProps {
  style: 'spinner' | 'dots' | 'pulse' | 'wave' | 'progress';
  color?: string;
  size?: 'small' | 'medium' | 'large';
  text?: string;
  showLogo?: boolean;
  logoUrl?: string;
}

const LoadingComponent: React.FC<LoadingComponentProps> = ({
  style,
  color = '#C5A06A',
  size = 'medium',
  text = 'Yükleniyor...',
  showLogo = false,
  logoUrl = '/qrmenu-small.webp'
}) => {
  const sizeMap = {
    small: { width: 40, height: 40, fontSize: '14px' },
    medium: { width: 60, height: 60, fontSize: '16px' },
    large: { width: 80, height: 80, fontSize: '18px' }
  };

  const currentSize = sizeMap[size];

  const renderSpinner = () => (
    <div
      style={{
        width: currentSize.width,
        height: currentSize.height,
        border: `4px solid ${color}20`,
        borderTop: `4px solid ${color}`,
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}
    />
  );

  const renderDots = () => (
    <div style={{ display: 'flex', gap: '8px' }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: '12px',
            height: '12px',
            backgroundColor: color,
            borderRadius: '50%',
            animation: `bounce 1.4s ease-in-out ${i * 0.16}s infinite both`
          }}
        />
      ))}
    </div>
  );

  const renderPulse = () => (
    <div
      style={{
        width: currentSize.width,
        height: currentSize.height,
        backgroundColor: color,
        borderRadius: '50%',
        animation: 'pulse 1.5s ease-in-out infinite'
      }}
    />
  );

  const renderWave = () => (
    <div style={{ display: 'flex', gap: '4px' }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            width: '4px',
            height: '40px',
            backgroundColor: color,
            animation: `wave 1.2s ease-in-out ${i * 0.1}s infinite`
          }}
        />
      ))}
    </div>
  );

  const renderProgress = () => (
    <div
      style={{
        width: '200px',
        height: '6px',
        backgroundColor: `${color}20`,
        borderRadius: '3px',
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: color,
          borderRadius: '3px',
          animation: 'progress 2s ease-in-out infinite'
        }}
      />
    </div>
  );

  const renderLoadingAnimation = () => {
    switch (style) {
      case 'spinner':
        return renderSpinner();
      case 'dots':
        return renderDots();
      case 'pulse':
        return renderPulse();
      case 'wave':
        return renderWave();
      case 'progress':
        return renderProgress();
      default:
        return renderSpinner();
    }
  };

  return (
    <div
      className="loading-component"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px',
        padding: '40px'
      }}
    >
      {showLogo && (
        <img
          src={logoUrl}
          alt="QR Menu"
          style={{
            width: '60px',
            height: '60px',
            objectFit: 'contain',
            marginBottom: '10px'
          }}
        />
      )}

      {renderLoadingAnimation()}

      {text && (
        <p
          style={{
            color: color,
            fontSize: currentSize.fontSize,
            fontWeight: '600',
            margin: 0,
            textAlign: 'center'
          }}
        >
          {text}
        </p>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }
        
        @keyframes pulse {
          0% {
            transform: scale(0.95);
            opacity: 0.7;
          }
          70% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(0.95);
            opacity: 0.7;
          }
        }
        
        @keyframes wave {
          0%, 40%, 100% {
            transform: scaleY(0.4);
          }
          20% {
            transform: scaleY(1);
          }
        }
        
        @keyframes progress {
          0% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        @media (max-width: 768px) {
          .loading-component {
            padding: clamp(24px, 6vw, 32px) !important;
            gap: clamp(16px, 4vw, 20px) !important;
          }
          
          .loading-component img {
            width: clamp(48px, 12vw, 60px) !important;
            height: clamp(48px, 12vw, 60px) !important;
          }
          
          .loading-component > div[style*="width: '200px'"] {
            width: clamp(150px, 37.5vw, 200px) !important;
          }
        }
        
        @media (max-width: 480px) {
          .loading-component {
            padding: 20px !important;
            gap: 12px !important;
          }
          
          .loading-component img {
            width: 40px !important;
            height: 40px !important;
          }
          
          .loading-component > div[style*="width: '200px'"] {
            width: 120px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default LoadingComponent;
